import { AU_DIVIDER_SOFT, AU_HOVER, AU_RADIUS_CONTROL, AU_SURFACE_2 } from '@/theme/au-compat';
import { Box, type SxProps, type Theme } from '@mui/material';
import { type FC } from 'react';

export interface TableProps {
  /**
   * El nombre accesible de la tabla.
   *
   * Un `div` con `role="table"` **no hereda** el nombre del título de la card que lo contiene: sin
   * esto un lector de pantalla anuncia «tabla» y nada más. No es opcional por eso — el rediseño
   * cambió a grilla justamente para ganar semántica, y una tabla sin nombre la pierde de vuelta.
   */
  label: string;
  headers: JSX.Element[];
  rows: {
    onClick?: () => void;
    columns: JSX.Element[];
  }[];
  /**
   * El reparto de columnas. Default: las tres del widget, en partes iguales.
   *
   * Se pasa desde afuera y no se deriva de `headers.length` porque el diálogo de todos los
   * movimientos lleva siete columnas con anchos distintos entre sí —el nombre es lo más largo, las
   * fechas piden ancho parecido, la posición solicitada es un número solo— y `1fr` para todas ahí
   * sería la grilla equivocada.
   */
  gridTemplateColumns?: string;
  /**
   * El aire entre columnas. Default: los 6px del widget.
   *
   * Va junto a `gridTemplateColumns` y por el mismo motivo: en el espécimen las dos salen de la
   * misma regla (`.oc-movements-table[data-detailed] .oc-movements-row`), porque el reparto y su
   * separación se deciden juntos — siete columnas separadas por el aire de tres quedan apretadas
   * aunque los `minmax` sean los correctos.
   */
  gap?: string;
  /**
   * El encabezado se queda arriba mientras las filas pasan por debajo.
   *
   * Es una prop y no un `sx` desde afuera porque el sticky **no se resuelve en un solo nodo**: la
   * fila de encabezado se pega, y de paso la caja tiene que dejar de recortar —un ancestro con
   * `overflow` es un contenedor de scroll, así que con el `overflow: hidden` del radio puesto el
   * encabezado se pegaría contra esta caja, que no scrollea, en vez de contra la que sí—. Quien la
   * enciende es responsable de poner el borde y el radio en el contenedor que scrollea, porque acá
   * se apagan.
   *
   * Lo usa el diálogo de todos los movimientos, donde una página de 25 filas y siete columnas es
   * ilegible sin los rótulos a la vista. El widget, de tres filas, no lo necesita.
   */
  stickyHeader?: boolean;
  /** Override de la caja de la tabla — lo consume el diálogo para reservar su alto. */
  sx?: SxProps<Theme>;
}

const ROW_SX = {
  display: 'grid',
  alignItems: 'center',
  px: '12px',
} as const;

/**
 * La tabla de movimientos.
 *
 * **Es una grilla con roles ARIA explícitos y no un `<table>`.** La grilla es lo que reparte las
 * columnas, y un `<table>` con `display: grid` pierde su semántica implícita — el navegador deja de
 * exponer filas y celdas. Los roles no dependen del `display`, así que puestos a mano dan las dos
 * cosas: el reparto de la grilla y una tabla que el lector de pantalla recorre por fila y columna.
 *
 * Antes era un `<table>` real con `border: 2`, `#35404A` de encabezado y `borderRadius: 2`. Lo que
 * cambia además del reparto es la escala: el filo baja a 1, el encabezado va en `--au-surface-2` y
 * el separador entre filas en `--au-divider-soft`, que es la mitad de marcado que el filo exterior
 * — separa dos filas de la misma superficie, no la superficie del fondo.
 *
 * **Vive en `gateCommon` y no en `metricsViews`**: la dibujan dos consumidores de features
 * distintas —el widget «Tabla de movimientos» del tablero y el diálogo de todos los movimientos—
 * con el mismo encabezado y el mismo reparto de columnas. `gateCommon` es la capa compartida del
 * dominio de compuertas, que es de donde los dos la toman.
 *
 * Espécimen: `digital-product-guidelines @ c7e4ea0` — `MovementsTable` y `.oc-movements-*`.
 */
export const Table: FC<TableProps> = ({
  label,
  headers,
  rows,
  gridTemplateColumns = '1fr 1fr 1fr',
  gap = '6px',
  stickyHeader = false,
  sx,
}) => {
  return (
    <Box
      role="table"
      aria-label={label}
      sx={[
        {
          width: '100%',
          // Con el encabezado pegado el filo y el radio los pone el contenedor que scrollea: acá
          // recortarían por dentro del área de scroll, así que la línea de arriba se iría con el
          // contenido y el radio no cortaría nada.
          ...(stickyHeader
            ? { border: 0, borderRadius: 0, overflow: 'visible' }
            : {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: AU_RADIUS_CONTROL,
                overflow: 'hidden',
              }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* El encabezado va un punto más apretado que las filas: sus dos líneas son 11,5 y 10px
          contra los 12 y 10,5 de una fila, así que el mismo padding lo dejaba más alto de lo que
          su contenido pide. */}
      <Box
        role="row"
        sx={{
          ...ROW_SX,
          gridTemplateColumns,
          gap,
          py: '4px',
          // Opaco, y no sólo cuando se pega: sobre un fondo translúcido las filas se leerían a
          // través del encabezado al pasar por debajo.
          bgcolor: AU_SURFACE_2,
          ...(stickyHeader && { position: 'sticky', top: 0, zIndex: 1 }),
        }}
      >
        {headers.map((header, idx) => (
          <Box
            key={idx}
            role="columnheader"
            // Sin alineación por índice: la trae cada header desde el call site.
            //
            // Era `idx === 0 ? 'left' : 'center'`, y funcionaba mientras «Solicitado por» fuera la
            // primera columna. El diálogo del PR 2 mete el ID adelante, y esa regla le habría dado
            // a un número la alineación pensada para un nombre que elide — sin fallar
            // ruidosamente: el ID se vería pegado a la izquierda y nadie lo llamaría bug.
            sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.primary', minWidth: 0 }}
          >
            {header}
          </Box>
        ))}
      </Box>

      {/* Las filas cuelgan **directo** de la tabla: un nodo sin rol entre `role="table"` y
          `role="row"` rompe la jerarquía ARIA y el lector deja de ver la fila como fila de esta
          tabla. Es lo que pasaba con el `Tooltip` por fila que esta tabla supo tener — envolvía
          cada fila en un `div` sin rol— y que se retiró con el diálogo: los tres datos que
          escondía son ahora columnas suyas, que es la diferencia entre un dato que existe para
          quien usa el mouse y uno que existe para todos. */}
      {rows.map(({ columns, onClick }, idx) => (
        <Box
          key={idx}
          role="row"
          onClick={onClick}
          sx={{
            ...ROW_SX,
            gridTemplateColumns,
            gap,
            py: '5px',
            borderTop: '1px solid',
            borderColor: AU_DIVIDER_SOFT,
            ...(onClick && { ':hover': { bgcolor: AU_HOVER, cursor: 'pointer' } }),
          }}
        >
          {columns.map((column, colIdx) => (
            <Box key={colIdx} role="cell" sx={{ minWidth: 0 }}>
              {column}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};
